import React, { useEffect } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  size?: 'md' | 'lg' | 'xl';
  children: React.ReactNode;
}
const widths = {
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-5xl'
};
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  size = 'md',
  children
}: ModalProps) {
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);
  const hidden = reduceMotion ? {
    opacity: 0
  } : {
    opacity: 0,
    scale: 0.96,
    y: 12
  };
  return <AnimatePresence data-id="element-340" data-mp="2wb5u2vecequj">
      {open && <motion.div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: 'easeOut'
    }} data-id="element-341" data-mp="2rb1b8ypypk15">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-342" data-mp="3f0cgaseci0l3" />
          <motion.div role="dialog" aria-modal="true" aria-label={title} className={`relative max-h-[92vh] w-full overflow-y-auto rounded-t-2xl border border-line bg-panel ${widths[size]} sm:rounded-2xl`} initial={hidden} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={hidden} transition={{
        duration: 0.24,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-343" data-mp="3a1j3712z0blg">
            <header className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-line bg-panel px-6 py-5" data-id="element-344" data-mp="3syk8yo4cmfqg">
              <div data-id="element-345" data-mp="1r4xkwijul43d">
                <h2 className="font-display text-2xl font-semibold leading-tight text-white" data-id="element-346" data-mp="1w1eie3cxsut4">{title}</h2>
                {subtitle && <p className="mt-0.5 text-sm text-muted" data-id="element-347" data-mp="1hdjquqao0whm">{subtitle}</p>}
              </div>
              <button type="button" onClick={onClose} aria-label="Cerrar" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-line text-muted transition-colors duration-150 hover:border-neon/60 hover:text-neon focus:outline-none focus-visible:ring-2 focus-visible:ring-neon" data-id="element-348" data-mp="1mbbf9ac3q0h2">
                <XIcon className="h-4 w-4" data-id="element-349" data-mp="13cqqvq1lh974" />
              </button>
            </header>
            <div className="p-6" data-id="element-350" data-mp="2srx8veiqmhpq">{children}</div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}